// index.js
import React, {Suspense} from "react";
import {
    BrowserRouter as Router,
    createRoutesFromChildren, matchRoutes,
    Route,
    Routes,
    useLocation,
    useNavigationType
} from "react-router-dom";
import {MutationCache, QueryCache, QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {createRoot} from "react-dom/client";
import {ConfigProvider} from "antd";
import Spinner from "./components/Spinner";
import "./App.css";
import {Amplify} from "aws-amplify";
import * as Sentry from "@sentry/react";

const App = React.lazy(() => import('./App'));

Amplify.configure({
    Auth: {
        Cognito: {
            identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
            allowGuestAccess: true,
            userPoolId: process.env.REACT_APP_USER_POOL_ID,
            userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID
        },
    },
});

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            const err =
                error instanceof Error
                    ? error
                    : new Error(`Query error: ${String(error)}`);

            Sentry.captureException(err, {
                tags: {
                    section: 'react-query',
                    type: 'query-error',
                },
                extra: {
                    queryKey: query.queryKey,
                    meta: query.meta,
                },
            });
        },
    }),

    mutationCache: new MutationCache({
        onError: (error, variables, _context, mutation) => {
            const err =
                error instanceof Error
                    ? error
                    : new Error(`Mutation error: ${String(error)}`);

            Sentry.captureException(err, {
                tags: {
                    section: 'react-query',
                    type: 'mutation-error',
                },
                extra: {
                    mutationKey: mutation.options.mutationKey,
                    variables,
                    meta: mutation.meta,
                },
            });
        },
    }),

    defaultOptions: {
        queries: {
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            retry: 0,
        },
        mutations: {
            retry: 0,
        },
    },
});

const PRIMARY_COLOR = '#005696';
const BOX_SHADOW = '0px 2px 4px rgba(0, 0, 0, 0.200448)';

const config = {
    token: {
        colorPrimary: PRIMARY_COLOR,
        borderRadius: 40,
        fontSize: 16,
        colorPrimaryHover: PRIMARY_COLOR,
    },
    components: {
        Button: {
            primaryShadow: BOX_SHADOW,
            defaultShadow: BOX_SHADOW,
            defaultColor: PRIMARY_COLOR,
        },
        Checkbox: {
            borderRadiusSM: 4
        }
    },
};

Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
    ],
    beforeSend(event) {
        if (process.env.REACT_APP_ENV === 'development' ||  process.env.REACT_APP_ENV === 'staging') {
            console.log('Sentry Event:', event);
        }
        return event;
    },
    tracesSampleRate: process.env.REACT_APP_ENV === 'production' ? 0.1 : 1.0,
    tracePropagationTargets: [process.env.REACT_APP_API_URL],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    normalizeDepth: 5,
    enabled: process.env.REACT_APP_ENV === 'production',
    release: process.env.REACT_APP_VERSION,
});

const root = createRoot(document.getElementById("root"));
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

root.render(
    <ConfigProvider theme={config}>
        <QueryClientProvider client={queryClient}>
                <Router>
                    <Suspense fallback={<Spinner/>}>
                        <SentryRoutes>
                            <Route path="/:token/*" element={<App/>}/>
                        </SentryRoutes>
                    </Suspense>
                </Router>
        </QueryClientProvider>
    </ConfigProvider>
);
